import { Meta } from './Meta';
import NextImage from 'next/image';
import AuthorCards from './AuthorCards';
import { Tags } from '@/components/Tags';
import { TextInput, Alert } from '@zopauk/web-ui';
import { buildModule } from '@/components/Modules';
import { StructuredText, Image } from 'react-datocms';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Placeholder } from '@/components/Placeholder';
import { getArticleBreadcrumbs } from '@/utils/breadcrumbs';
import type { ArticleContext } from '@/utils/models/article';
import type { Document } from 'datocms-structured-text-utils';
import { customNodeRules, renderBlock } from '@/components/structuredText';
import { type ArticleContentFragment, useArticleQuery } from '@/utils/dato/generated';

// Blocks in the article body are told they are in an article so full-page modules (e.g. the FAQ accordion) can
// fit the article column instead of their page-level layout
const renderArticleBlock: typeof renderBlock = ({ record }) =>
  buildModule({ module: record as Parameters<typeof buildModule>[0]['module'], context: { inArticle: true } });

const Content = ({ pageContext }: { pageContext: ArticleContext } & ArticleContentFragment) => {
  const { error, data } = useArticleQuery({ slug: pageContext.slug });

  if (data?.article) {
    const { article } = data;

    return (
      <div className="flex flex-col">
        <div className="bg-quarterary w-full">
          <div className="max-w-[648px] mx-auto px-4 pt-10">
            <Breadcrumbs breadcrumbs={getArticleBreadcrumbs(article)} className="mb-6" />

            <h1 className="text-4xl lg:text-5xl font-serif font-semibold overflow-wrap:break-word">{article.title}</h1>

            <div className="w-full mt-8 mb-8">
              <Meta article={{ ...article, hideRelativeTime: true, hideCategories: true }} />
            </div>

            {article.featuredImage ? (
              <div className="relative -mb-[172px]">
                {article.featuredImage.responsiveImage ? (
                  <Image
                    data={article.featuredImage.responsiveImage}
                    style={{ margin: '0 auto' }}
                    objectPosition="50% 50%"
                  />
                ) : (
                  <div
                    style={{
                      position: 'relative',
                      aspectRatio: '16/9',
                      margin: '0 auto',
                      height: 'auto',
                      width: '100%',
                    }}
                  >
                    <NextImage
                      alt={article.featuredImage.title || 'Featured image'}
                      style={{ objectFit: 'contain' }}
                      src={article.featuredImage.url}
                      unoptimized
                      fill
                    />
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>

        <div className="relative mb-8 lg:mb-11 flex flex-col max-w-screen-xl mx-auto w-full px-4">
          <div className={`max-w-[648px] mx-auto w-full ${article.featuredImage ? 'mt-[172px]' : 'mt-8'}`}>
            {article?.body ? (
              <div className="px-5 md:mx-auto mt-8">
                <StructuredText
                  data={{
                    value: article.body.value as Document,
                    blocks: article.body.blocks,
                  }}
                  customNodeRules={customNodeRules}
                  renderBlock={renderArticleBlock}
                />
                <Tags tags={article.tags} />
                <form className="xl:hidden mt-4" action="/blog/search" autoComplete="off" method="get">
                  <TextInput
                    startIcon={<span className="material-symbols-outlined">search</span>}
                    placeholder="Search blog..."
                    id="blog-search-mobile"
                    name="search"
                    type="search"
                  />
                </form>
              </div>
            ) : null}

            <AuthorCards contributors={article.contributors} author={article.author} />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative mt-10 mb-8 lg:mb-11 flex flex-col max-w-screen-xl mx-auto w-full px-4">
        <Alert variant="warning">We've been unable to load this Blog post</Alert>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="bg-quarterary w-full">
        <div className="max-w-[648px] mx-auto px-4 pt-10 pb-10">
          <Placeholder className="w-24 h-8 rounded-full mb-6" />
          <Placeholder className="h-[100px]" />
          <Placeholder className="h-[30px] mt-8 mb-8" />
        </div>
      </div>
      <div className="mt-8 mb-8 lg:mb-11 max-w-screen-xl mx-auto px-4">
        <div className="pt-4 mb-8">
          <Placeholder className="h-[350px]" />
          <Placeholder className="h-[100px] mt-8" />
          <Placeholder className="h-[100px]" />
          <Placeholder className="h-[100px]" />
        </div>
      </div>
    </div>
  );
};

export default Content;
